import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ShieldAlert,
  ShieldCheck,
  RefreshCcw,
  AlertTriangle,
  Lock,
  FileWarning,
  Activity,
  Check,
  Undo2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";


export const Route = createFileRoute("/_authenticated/security")({
  component: SecurityCenterPage,
  head: () => ({ meta: [{ title: "Security center — MissionReady" }] }),
});

type EventRow = {
  id: string;
  organization_id: string | null;
  actor_id: string | null;
  event_type: string;
  severity: "info" | "warning" | "critical";
  source: string | null;
  route: string | null;
  target_org_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  acknowledgement_note: string | null;
};

type Bucket = { bucket: string; event_type: string; severity: string; count: number };

const WINDOWS = {
  "24h": { hours: 24, label: "Last 24 hours" },
  "7d":  { hours: 24 * 7, label: "Last 7 days" },
  "30d": { hours: 24 * 30, label: "Last 30 days" },
} as const;
type WindowKey = keyof typeof WINDOWS;

const TYPE_META: Record<string, { label: string; icon: typeof ShieldAlert; tone: string }> = {
  forbidden:          { label: "Forbidden",            icon: Lock,         tone: "text-amber-600" },
  storage_denied:     { label: "Storage denied",       icon: FileWarning,  tone: "text-amber-600" },
  app_events_blocked: { label: "Telemetry blocked",    icon: ShieldAlert,  tone: "text-amber-600" },
  rls_violation:      { label: "RLS violation",        icon: ShieldAlert,  tone: "text-red-600" },
  cross_org_write:    { label: "Cross-org write",      icon: AlertTriangle,tone: "text-red-600" },
  signature_invalid:  { label: "Invalid signature",    icon: ShieldAlert,  tone: "text-red-600" },
  replay_blocked:     { label: "Replay blocked",       icon: ShieldAlert,  tone: "text-amber-600" },
  rate_limited:       { label: "Rate limited",         icon: Activity,     tone: "text-muted-foreground" },
  other:              { label: "Other",                icon: ShieldAlert,  tone: "text-muted-foreground" },
};

function SecurityCenterPage() {
  const { currentOrg, currentRole } = useAuth();
  const canView = currentRole === "org_owner" || currentRole === "admin";

  const [windowKey, setWindowKey] = useState<WindowKey>("24h");
  const [statusFilter, setStatusFilter] = useState<"all" | "open" | "acknowledged">("open");
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [rows, setRows] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ackDialog, setAckDialog] = useState<{ ids: string[] } | null>(null);
  const [ackNote, setAckNote] = useState("");
  const [acking, setAcking] = useState(false);

  const orgId = currentOrg?.id ?? null;

  async function load() {
    if (!orgId || !canView) return;
    setLoading(true);
    const fromIso = new Date(Date.now() - WINDOWS[windowKey].hours * 3600_000).toISOString();
    const toIso = new Date().toISOString();
    const [{ data: rollup }, { data: list }] = await Promise.all([
      supabase.rpc("security_event_rollup" as never, {
        _organization_id: orgId, _from: fromIso, _to: toIso,
      } as never),
      supabase.from("security_events")
        .select("*")
        .eq("organization_id", orgId)
        .gte("created_at", fromIso)
        .order("created_at", { ascending: false })
        .limit(200),
    ]);
    setBuckets((rollup ?? []) as Bucket[]);
    setRows((list ?? []) as EventRow[]);
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orgId, windowKey, canView]);

  // Live updates — react to INSERT (new event) and UPDATE (ack toggle)
  useEffect(() => {
    if (!orgId || !canView) return;
    const ch = supabase
      .channel(`security_events:${orgId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "security_events", filter: `organization_id=eq.${orgId}` },
        (payload) => setRows((prev) => [payload.new as EventRow, ...prev].slice(0, 200)),
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "security_events", filter: `organization_id=eq.${orgId}` },
        (payload) => {
          const next = payload.new as EventRow;
          setRows((prev) => prev.map((r) => (r.id === next.id ? next : r)));
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, canView]);

  const visibleRows = useMemo(() => rows.filter((r) => {
    if (statusFilter === "open") return !r.acknowledged_at;
    if (statusFilter === "acknowledged") return !!r.acknowledged_at;
    return true;
  }), [rows, statusFilter]);

  const openCount = useMemo(() => rows.filter((r) => !r.acknowledged_at).length, [rows]);

  function toggleSel(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function performAck(ids: string[], note: string | null, unack: boolean) {
    if (ids.length === 0) return;
    setAcking(true);
    try {
      const { data, error } = await supabase.rpc("ack_security_event" as never, {
        _event_ids: ids, _note: note, _unack: unack,
      } as never);
      if (error) throw error;
      const n = (data as number) ?? 0;
      toast.success(unack ? `Reopened ${n} event(s)` : `Acknowledged ${n} event(s)`);
      setSelected(new Set());
      setAckDialog(null);
      setAckNote("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Action failed");
    } finally {
      setAcking(false);
    }
  }


  const totals = useMemo(() => {
    const t: Record<string, number> = {};
    for (const b of buckets) t[b.event_type] = (t[b.event_type] ?? 0) + Number(b.count);
    return t;
  }, [buckets]);

  const critical = useMemo(() => buckets
    .filter(b => b.severity === "critical")
    .reduce((s, b) => s + Number(b.count), 0), [buckets]);

  const hourly = useMemo(() => {
    // bucket -> total
    const map = new Map<string, number>();
    for (const b of buckets) map.set(b.bucket, (map.get(b.bucket) ?? 0) + Number(b.count));
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [buckets]);

  const max = Math.max(1, ...hourly.map(([, v]) => v));

  async function runDetector() {
    if (!orgId) return;
    setDetecting(true);
    try {
      const { data, error } = await supabase.rpc("detect_security_alerts" as never, {
        _organization_id: orgId, _window_minutes: 60,
      } as never);
      if (error) throw error;
      const a = ((data as { alerts?: unknown[] })?.alerts ?? []) as unknown[];
      if (a.length === 0) toast.success("No alerts triggered in the last hour.");
      else toast.warning(`${a.length} alert(s) dispatched to org owners & admins.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Detector failed");
    } finally {
      setDetecting(false);
    }
  }

  if (!canView) {
    return (
      <div className="container max-w-3xl py-10">
        <Card className="p-6 text-sm text-muted-foreground">
          Security center is restricted to organization owners and admins.
        </Card>
      </div>
    );
  }

  return (
    <div className="container w-full max-w-6xl overflow-x-hidden px-4 py-6 space-y-5 md:px-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-xl font-semibold">Security center</h1>
            <p className="text-sm text-muted-foreground">
              Suspicious cross-tenant access attempts, denied storage downloads, and telemetry pollution.
            </p>
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Select value={windowKey} onValueChange={(v) => setWindowKey(v as WindowKey)}>
            <SelectTrigger className="w-full min-w-0 sm:w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(WINDOWS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={load} disabled={loading}>
            <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" className="flex-1 sm:flex-none" onClick={runDetector} disabled={detecting}>
            <ShieldAlert className="mr-2 h-4 w-4" /> {detecting ? "Running…" : "Run alert detector"}
          </Button>
        </div>
      </header>



      {critical > 0 && (
        <Card className="border-red-500/40 bg-red-500/5 p-4 text-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            <div>
              <div className="font-semibold text-red-700">
                {critical} critical security event{critical === 1 ? "" : "s"} in {WINDOWS[windowKey].label.toLowerCase()}.
              </div>
              <div className="text-muted-foreground">
                Review the timeline below. Run the alert detector to notify all owners and admins.
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Object.entries(TYPE_META).slice(0, 8).map(([k, m]) => {
          const Icon = m.icon;
          const n = totals[k] ?? 0;
          return (
            <Card key={k} className="min-w-0 p-3 sm:p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 break-words text-[11px] uppercase tracking-wider text-muted-foreground sm:text-xs">{m.label}</div>
                <Icon className={`h-4 w-4 shrink-0 ${m.tone}`} />
              </div>
              <div className="mt-2 font-display text-2xl font-bold">{n}</div>
            </Card>
          );
        })}
      </div>

      <Card className="min-w-0 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="text-sm font-semibold">Events over time</div>
          <div className="text-xs text-muted-foreground">{hourly.length} hourly buckets</div>
        </div>
        {hourly.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No security events in this window 🎉</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex h-32 min-w-full items-end gap-1">
              {hourly.map(([bucket, count]) => (
                <div
                  key={bucket}
                  className="min-w-[4px] flex-1 rounded-t bg-primary/70 hover:bg-primary"
                  style={{ height: `${(count / max) * 100}%` }}
                  title={`${new Date(bucket).toLocaleString()} — ${count} event(s)`}
                />
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card className="min-w-0 p-0 overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <div className="text-sm font-semibold">Recent events</div>
            <span className="text-xs text-muted-foreground">
              {openCount} open · {rows.length - openCount} acknowledged
            </span>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="h-8 w-full min-w-0 sm:w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              className="w-full sm:w-auto"
              disabled={selected.size === 0 || acking}
              onClick={() => setAckDialog({ ids: Array.from(selected) })}
            >
              <Check className="mr-2 h-4 w-4" /> Acknowledge ({selected.size})
            </Button>
          </div>
        </div>

        <div className="divide-y divide-border">
          {loading ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">Loading…</div>
          ) : visibleRows.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">Nothing to report.</div>
          ) : visibleRows.map((r) => {
            const meta = TYPE_META[r.event_type] ?? TYPE_META.other;
            const Icon = meta.icon;
            const isAcked = !!r.acknowledged_at;
            return (
              <div key={r.id} className={`flex items-start gap-2 px-3 py-3 text-sm sm:gap-3 sm:px-4 ${isAcked ? "bg-muted/30" : ""}`}>
                {!isAcked ? (
                  <Checkbox
                    className="mt-1 shrink-0"
                    checked={selected.has(r.id)}
                    onCheckedChange={() => toggleSel(r.id)}
                    aria-label="Select event"
                  />
                ) : (
                  <div className="mt-1 h-4 w-4 shrink-0" />
                )}
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${meta.tone}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="font-medium">{meta.label}</span>
                    <span className="text-xs text-muted-foreground">{r.source ?? "—"}</span>
                    {r.route && <span className="min-w-0 max-w-full truncate text-xs text-muted-foreground">{r.route}</span>}
                    {isAcked && (
                      <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                        Reviewed
                      </span>
                    )}
                    <span className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase sm:ml-auto ${
                      r.severity === "critical" ? "bg-red-500/15 text-red-700" :
                      r.severity === "warning" ? "bg-amber-500/15 text-amber-700" :
                      "bg-muted text-muted-foreground"
                    }`}>{r.severity}</span>
                  </div>
                  <div className="break-words text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()} · actor {r.actor_id?.slice(0, 8) ?? "—"}
                    {r.target_org_id && <> · target org {r.target_org_id.slice(0, 8)}</>}
                  </div>
                  {isAcked && (
                    <div className="mt-1 break-words text-xs text-emerald-700">
                      Acknowledged {new Date(r.acknowledged_at!).toLocaleString()}
                      {r.acknowledged_by && <> by {r.acknowledged_by.slice(0, 8)}</>}
                      {r.acknowledgement_note && (
                        <span className="ml-1 italic text-muted-foreground">— {r.acknowledgement_note}</span>
                      )}
                    </div>
                  )}
                  {r.metadata && Object.keys(r.metadata).length > 0 && (
                    <pre className="mt-1 max-h-32 max-w-full overflow-auto whitespace-pre-wrap break-words rounded bg-muted/50 p-2 text-[11px] text-muted-foreground">
                      {JSON.stringify(r.metadata, null, 2)}
                    </pre>
                  )}
                  <div className="mt-1 sm:hidden">
                    {!isAcked ? (
                      <Button size="sm" variant="outline" disabled={acking} onClick={() => setAckDialog({ ids: [r.id] })}>
                        <Check className="mr-1 h-4 w-4" /> Acknowledge
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled={acking} onClick={() => performAck([r.id], null, true)}>
                        <Undo2 className="mr-1 h-4 w-4" /> Reopen
                      </Button>
                    )}
                  </div>
                </div>
                <div className="hidden shrink-0 sm:block">
                  {!isAcked ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={acking}
                      onClick={() => setAckDialog({ ids: [r.id] })}
                    >
                      <Check className="mr-1 h-4 w-4" /> Ack
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={acking}
                      onClick={() => performAck([r.id], null, true)}
                    >
                      <Undo2 className="mr-1 h-4 w-4" /> Reopen
                    </Button>
                  )}
                </div>
              </div>

            );
          })}
        </div>
      </Card>

      <Dialog open={!!ackDialog} onOpenChange={(o) => { if (!o) { setAckDialog(null); setAckNote(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acknowledge {ackDialog?.ids.length ?? 0} event(s)</DialogTitle>
            <DialogDescription>
              Mark as reviewed so they're hidden from the open queue. Add an optional note for the audit trail.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={ackNote}
            onChange={(e) => setAckNote(e.target.value)}
            placeholder="Investigation notes, follow-up, or 'false positive — known scanner'."
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAckDialog(null); setAckNote(""); }}>Cancel</Button>
            <Button
              onClick={() => ackDialog && performAck(ackDialog.ids, ackNote.trim() || null, false)}
              disabled={acking}
            >
              {acking ? "Saving…" : "Mark as reviewed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
